/* eslint-disable max-statements */
import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import { fetchCosmeticConfigs, fetchCosmeticHistory } from '@profile/src/redux/actions';
import { useTypedDispatch, useTypedSelector } from '@profile/src/redux/utils';
import EventMultipleSelect from './Components/EventMultipleSelect';
import { COUNTDOWN_TIME_REFRESH, DEFAULT_ITEM_PER_PAGE } from './constants';
import RefreshButton from '../Components/RefreshButton';
import BreakpointContent from '@profile/src/Components/BreakpointContent';
import PaginationControl from '@profile/src/Components/PaginationControl_V2';
import PageContent from '@profile/src/Components/PageContent';
import TableHeader from './Components/TableHeader';
import { AppError } from '@profile/src/redux/types';
import EmptyList from '@profile/src/Components/EmptyList';
import Spinner from '@profile/src/Components/Spinner';
import { normaliseSearchUrl } from '@profile/src/utils/query';
import useParamsRoute from '@profile/src/hooks/useRouteParams';
import ListItemDesktop from './Components/ListItemDesktop';
import ListItemPhone from './Components/ListItemPhone';
import { TimeFilterType } from '@profile/src/utils/types';
import { selectListCosmeticHistory } from './selectors';
import { CategoryKey, MenuItem, navigateToHistoryPage } from '@profile/src/utils/navigate';
import TimeSingleSelect from '../Components/TimeSingleSelect';

const CosmeticHistoryTab = () => {
  const dispatch = useTypedDispatch();
  const {
    page,
    type,
    time = TimeFilterType.TIME_DAY_7
  } = useParamsRoute();
  const selectedEvents = React.useMemo(() => {
    return type ? type.toString().split(',').map(e => Number(e)) : [];
  }, [type]);
  const items = useTypedSelector(selectListCosmeticHistory);
  const totalItems = useTypedSelector(state => state.cosmeticHistory.totalItems || 0);
  const loading = useTypedSelector(state => state.cosmeticHistory.loading);
  const invalidated = useTypedSelector(state => state.cosmeticHistory.invalidated);
  const error = useTypedSelector(state => state.cosmeticHistory.error !== AppError.NO_ERROR);
  const totalPages = Math.ceil(totalItems / DEFAULT_ITEM_PER_PAGE) || 1;
  const onFetch = React.useCallback((page, eventsFilter, timeFilter) => {
    dispatch(fetchCosmeticHistory({
      page: page || 1,
      size: DEFAULT_ITEM_PER_PAGE,
      time: timeFilter,
      type: eventsFilter
    }));
  }, [dispatch]);

  const pushSearchUrl = urlSearch => {
    navigateToHistoryPage({
      urlSearch,
      tab: MenuItem.HISTORY,
      category: CategoryKey.CosmeticHistory
    });
  };

  const onPageChange = nextPage => {
    const urlSearch = normaliseSearchUrl({
      page: nextPage,
      time,
      type
    });
    pushSearchUrl(urlSearch);
  };

  const onEventChanged = ids => {
    const urlSearch = normaliseSearchUrl({
      page: 1,
      time,
      type: ids.length > 0 ? ids.join(',') : undefined
    });
    pushSearchUrl(urlSearch);
  };

  const onTimeChanged = id => {
    const urlSearch = normaliseSearchUrl({
      page: 1,
      time: id ? id : undefined,
      type: type
    });
    pushSearchUrl(urlSearch);
  };

  const onRefresh = () => {
    onFetch(page, type, time);
  };

  React.useEffect(() => {
    dispatch(fetchCosmeticConfigs());
  }, [dispatch]);
  React.useEffect(() => {
    onFetch(page, type, time);
  }, [onFetch, page, time, type]);

  const renderContent = () => {
    if (!items.length) {
      return /*#__PURE__*/React.createElement(EmptyList, {
        title: "Sorry, no record found.",
        className: styles.empty
      });
    }

    return /*#__PURE__*/React.createElement(BreakpointContent, {
      renderPhone: () => /*#__PURE__*/React.createElement(ListItemPhone, {
        data: items
      }),
      renderTablet: () => /*#__PURE__*/React.createElement(ListItemDesktop, {
        data: items
      }),
      renderDesktop: () => /*#__PURE__*/React.createElement(ListItemDesktop, {
        data: items
      })
    });
  };

  const renderError = () => {
    return /*#__PURE__*/React.createElement(EmptyList, {
      title: "Sorry, something went wrong.",
      content: "Please try again.",
      className: styles.empty
    });
  };

  const renderLoading = () => {
    return /*#__PURE__*/React.createElement(Spinner, {
      className: styles.spinner
    });
  };

  const renderDesktop = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: styles.wrapperHeader
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.header
    }, /*#__PURE__*/React.createElement(EventMultipleSelect, {
      className: styles.filter,
      onSelect: onEventChanged,
      values: selectedEvents
    }), /*#__PURE__*/React.createElement(TimeSingleSelect, {
      className: styles.filter,
      onSelect: onTimeChanged,
      value: time === null || time === void 0 ? void 0 : time.toString()
    }), /*#__PURE__*/React.createElement(RefreshButton, {
      limitCountTime: COUNTDOWN_TIME_REFRESH,
      className: styles.refreshButton,
      onClick: onRefresh
    }))), /*#__PURE__*/React.createElement("div", {
      className: styles.table
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.tableBody
    }, /*#__PURE__*/React.createElement(TableHeader, null), /*#__PURE__*/React.createElement(PageContent, {
      invalidated: invalidated,
      loading: loading,
      error: error,
      renderContent: renderContent,
      renderLoading: renderLoading,
      renderError: renderError
    })), /*#__PURE__*/React.createElement(PaginationControl, {
      totalPages: totalPages,
      page: typeof page === 'number' ? page : 1,
      onPageChange: onPageChange,
      className: styles.paging
    })));
  };

  const renderMobile = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: styles.wrapperHeader
    }, /*#__PURE__*/React.createElement(EventMultipleSelect, {
      className: styles.event,
      onSelect: onEventChanged,
      values: selectedEvents
    }), /*#__PURE__*/React.createElement("div", {
      className: styles.header
    }, /*#__PURE__*/React.createElement(TimeSingleSelect, {
      className: styles.filter,
      onSelect: onTimeChanged,
      value: time === null || time === void 0 ? void 0 : time.toString()
    }), /*#__PURE__*/React.createElement(RefreshButton, {
      limitCountTime: 5,
      className: styles.refreshButton,
      onClick: onRefresh
    }))), /*#__PURE__*/React.createElement("div", {
      className: styles.table
    }, /*#__PURE__*/React.createElement(PageContent, {
      invalidated: invalidated,
      loading: loading,
      error: error,
      renderContent: renderContent,
      renderLoading: renderLoading,
      renderError: renderError
    }), /*#__PURE__*/React.createElement(PaginationControl, {
      totalPages: totalPages,
      page: typeof page === 'number' ? page : 1,
      onPageChange: onPageChange,
      className: classNames(styles.paging, styles.mobile)
    })));
  };

  return /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.container)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.wrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.container
  }, /*#__PURE__*/React.createElement(BreakpointContent, {
    renderDesktop: renderDesktop,
    renderPhone: renderMobile,
    renderTablet: renderDesktop
  }))));
};

CosmeticHistoryTab.displayName = 'CosmeticHistoryTab';
export default CosmeticHistoryTab;